import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';

import { createAuthSlice, type AuthSlice } from './slices/authSlice';
import { createUISlice, type UISlice } from './slices/uiSlice';
import { createNotificationSlice, type NotificationSlice } from './slices/notificationSlice';
import { getAccessToken, setAuthFailureHandler } from '@/lib/api-client';

// Hydration state
let hasHydrated = false;
const hydrationListeners: Set<(hydrated: boolean) => void> = new Set();

export const getHasHydrated = () => hasHydrated;

// Subscribe to hydration state changes
const subscribeToHydration = (callback: (hydrated: boolean) => void) => {
  hydrationListeners.add(callback);
  // If already hydrated, call immediately
  if (hasHydrated) {
    callback(true);
  }
  return () => {
    hydrationListeners.delete(callback);
  };
};

// Notify all listeners when hydration completes
const notifyHydrationComplete = () => {
  hasHydrated = true;
  hydrationListeners.forEach((listener) => listener(true));
};

export const useHasHydrated = () => {
  const [hydrated, setHydrated] = useState(hasHydrated);

  useEffect(() => {
    // If already hydrated, set immediately
    if (hasHydrated) {
      setHydrated(true);
      return;
    }

    // Subscribe to hydration changes
    const unsubscribe = subscribeToHydration((isHydrated) => {
      setHydrated(isHydrated);
    });

    return unsubscribe;
  }, []);

  return hydrated;
};

// Combined store type
export type BoundStore = AuthSlice & UISlice & NotificationSlice;

// Create the bound store with all slices merged
export const useBoundStore = create<BoundStore>()(
  devtools(
    persist(
      (...args) => ({
        ...createAuthSlice(...args),
        ...createUISlice(...args),
        ...createNotificationSlice(...args),
      }),
      {
        name: 'unitree-storage',
        partialize: (state) => ({
          // Auth - persist user session (isAuthenticated is NOT persisted;
          // it is derived from the persisted user object on rehydration)
          user: state.user,
          tenant: state.tenant,
          // UI - persist preferences
          isDarkMode: state.isDarkMode,
          isSidebarCollapsed: state.isSidebarCollapsed,
        }),
        onRehydrateStorage: () => (state) => {
          // Apply dark mode on rehydration
          if (state?.isDarkMode) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
          // Derive isAuthenticated from persisted user only when a usable token exists.
          // This prevents stale localStorage user data from rendering an authenticated shell.
          if (state?.user && getAccessToken()) {
            useBoundStore.setState({ isAuthenticated: true });
          } else if (state?.user) {
            useBoundStore.getState().logout();
          }

          // Mark hydration as complete and notify all subscribers
          notifyHydrationComplete();

          // Set up auth failure handler to clear store on token expiry
          setAuthFailureHandler(() => {
            useBoundStore.getState().logout();
          });
        },
      },
    ),
    {
      name: 'unitree-store',
    },
  ),
);

// =============================================================================
// AUTH SELECTORS
// =============================================================================

export const useUser = () => useBoundStore((state) => state.user);
export const useTenant = () => useBoundStore((state) => state.tenant);
export const useIsAuthenticated = () => useBoundStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useBoundStore((state) => state.isLoading);
export const useAuthError = () => useBoundStore((state) => state.error);

// Auth actions - use useShallow to prevent infinite re-renders
export const useAuthActions = () =>
  useBoundStore(
    useShallow((state) => ({
      setUser: state.setUser,
      setTenant: state.setTenant,
      setLoading: state.setLoading,
      setError: state.setError,
      login: state.login,
      logout: state.logout,
      updateUser: state.updateUser,
      clearError: state.clearError,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasRole: state.hasRole,
    })),
  );

// =============================================================================
// UI SELECTORS
// =============================================================================

export const useIsDarkMode = () => useBoundStore((state) => state.isDarkMode);
export const useIsSidebarOpen = () => useBoundStore((state) => state.isSidebarOpen);
export const useIsSidebarCollapsed = () => useBoundStore((state) => state.isSidebarCollapsed);
export const useIsMobileSidebarOpen = () => useBoundStore((state) => state.isMobileSidebarOpen);
export const useNotificationCount = () => useBoundStore((state) => state.notificationCount);
export const useGlobalLoading = () => useBoundStore((state) => state.globalLoading);

// UI actions - use useShallow to prevent infinite re-renders
export const useUIActions = () =>
  useBoundStore(
    useShallow((state) => ({
      toggleDarkMode: state.toggleDarkMode,
      setDarkMode: state.setDarkMode,
      toggleSidebar: state.toggleSidebar,
      setSidebarOpen: state.setSidebarOpen,
      toggleSidebarCollapse: state.toggleSidebarCollapse,
      setSidebarCollapsed: state.setSidebarCollapsed,
      toggleMobileSidebar: state.toggleMobileSidebar,
      setMobileSidebarOpen: state.setMobileSidebarOpen,
      setNotificationCount: state.setNotificationCount,
      incrementNotificationCount: state.incrementNotificationCount,
      clearNotifications: state.clearNotifications,
      setGlobalLoading: state.setGlobalLoading,
    })),
  );

// =============================================================================
// NOTIFICATION SELECTORS
// =============================================================================

export const useNotifications = () => useBoundStore((state) => state.notifications);
export const useUnreadNotificationCount = () => useBoundStore((state) => state.unreadCount);
export const useNotificationLoading = () => useBoundStore((state) => state.isLoading);
export const useNotificationPanelOpen = () => useBoundStore((state) => state.isOpen);

// Notification actions - use useShallow to prevent infinite re-renders
export const useNotificationActions = () =>
  useBoundStore(
    useShallow((state) => ({
      setNotifications: state.setNotifications,
      addNotification: state.addNotification,
      markAsRead: state.markAsRead,
      markAllAsRead: state.markAllAsRead,
      removeNotification: state.removeNotification,
      clearAll: state.clearAll,
      setUnreadCount: state.setUnreadCount,
      setLoading: state.setLoading,
      setError: state.setError,
      setOpen: state.setOpen,
      toggleOpen: state.toggleOpen,
    })),
  );

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type { User, Tenant, UserRole, Permission, AuthSlice } from './slices/authSlice';
export type { UISlice } from './slices/uiSlice';
export type { Notification, NotificationSlice } from './slices/notificationSlice';
